import { constObjectToOptions } from 'noddi-util';
import { z } from 'zod';
import { refineDiscountBrands } from '../../discountBrands';
import { DISCOUNT_KIND } from '../../discountKind';
import { defaultPartnerDiscountUsageFormValues, DISCOUNT_USAGE_KIND } from '../../discountTerms';
import { refinePromotionDealFields } from '../../promotionDealValidation';
import { bookingExclusionRowSchema, refineBookingExclusions } from '../bookingExclusion';

export const CAMPAIGN_AUDIENCE = {
  anyone: 'anyone',
  new_customers: 'new_customers'
} as const;

export const CAMPAIGN_AUDIENCE_OPTIONS = constObjectToOptions(CAMPAIGN_AUDIENCE, {
  anyone: 'Anyone',
  new_customers: 'New customers only'
});

const discountSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    discountKind: z.enum([DISCOUNT_KIND.amount, DISCOUNT_KIND.percentage]),
    discountAmount: z.number().nullable(),
    discountPercentage: z.number().nullable(),
    appliesToAllProducts: z.boolean(),
    filterByProducts: z.boolean(),
    filterByTags: z.boolean(),
    salesItemIds: z.array(z.number()),
    salesItemTagIds: z.array(z.number()),
    limitToBrands: z.boolean(),
    applicableBrandIds: z.array(z.number()),
    usageKind: z.enum([DISCOUNT_USAGE_KIND.once, DISCOUNT_USAGE_KIND.limited, DISCOUNT_USAGE_KIND.forever]),
    maxUsesCount: z.number().nullable(),
    bookingExclusions: z.array(bookingExclusionRowSchema)
  })
  .superRefine(refinePromotionDealFields)
  .superRefine(refineDiscountBrands)
  .superRefine(refineBookingExclusions);

export const createCampaignSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  code: z.string(),
  authCode: z.string(),
  image: z.instanceof(File).nullable().optional(),
  expiryDaysAfterReceiving: z.number().int().positive().nullable(),
  expiresAt: z.date().nullish(),
  audienceKind: z.enum([CAMPAIGN_AUDIENCE.anyone, CAMPAIGN_AUDIENCE.new_customers]),
  discounts: z.array(discountSchema).min(1, 'Add at least one discount')
});

export type CreateCampaignFormValues = z.input<typeof createCampaignSchema>;
export type CreateCampaignDiscountValues = z.input<typeof discountSchema>;

export const createEmptyCampaignDiscount = (): CreateCampaignDiscountValues => {
  const usage = defaultPartnerDiscountUsageFormValues();
  return {
    name: '',
    description: '',
    discountKind: DISCOUNT_KIND.amount,
    discountAmount: null,
    discountPercentage: null,
    appliesToAllProducts: true,
    filterByProducts: false,
    filterByTags: false,
    salesItemIds: [],
    salesItemTagIds: [],
    limitToBrands: false,
    applicableBrandIds: [],
    bookingExclusions: [],
    usageKind: usage.usageKind,
    maxUsesCount: usage.maxUsesCount
  };
};

export const createCampaignDefaultValues = (): CreateCampaignFormValues => ({
  name: '',
  description: '',
  code: '',
  authCode: '',
  image: undefined,
  expiryDaysAfterReceiving: null,
  expiresAt: null,
  audienceKind: CAMPAIGN_AUDIENCE.anyone,
  discounts: [createEmptyCampaignDiscount()]
});
